import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Star, Trash2 } from 'lucide-react';
import axios from 'axios';
import {
  PageHeader,
  ConfirmDialog,
  LoadingState,
  DataTable,
} from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, { headers: authHeaders() });
      setReviews(r.data.reviews || r.data || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchReviews(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestDelete = (id) => {
    setConfirmId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmLoading(true);
    try {
      await axios.delete(`${API}/admin/reviews/${confirmId}`, { headers: authHeaders() });
      toast.success(t('admin.rev_deleted'));
      fetchReviews();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmId(null);
    }
  };

  // render signature: (cellValue, fullRow)
  const columns = [
    {
      key: 'user',
      label: t('admin.pay_col_user'),
      render: (_val, row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">{row.user_name?.[0]}</span>
          </div>
          <span className="font-semibold text-sm">{row.user_name}</span>
        </div>
      ),
    },
    {
      key: 'rating',
      label: t('admin.rev_col_rating'),
      render: (val) => (
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, j) => (
            <Star
              key={j}
              size={12}
              fill={j < val ? 'var(--brand-secondary)' : 'none'}
              className={j < val ? 'text-[var(--brand-secondary)]' : 'text-gray-200'}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'comment',
      label: t('admin.rev_col_comment'),
      render: (val) => <p className="text-xs text-[var(--text-muted)] max-w-xs truncate">{val}</p>,
    },
    {
      key: 'course_title',
      label: t('admin.pay_col_course'),
      render: (val) => <span className="text-xs">{val}</span>,
    },
    {
      key: 'created_at',
      label: t('admin.pay_col_date'),
      render: (val) => (
        <span className="text-xs text-[var(--text-muted)]">
          {val ? new Date(val).toLocaleDateString('tr-TR') : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_val, row) => (
        <button
          onClick={() => requestDelete(row.review_id)}
          className="p-1.5 rounded bg-red-50 text-red-500 hover:bg-red-100"
          aria-label={t('admin.btn_delete')}
          data-testid={`del-review-${row.review_id}`}
        >
          <Trash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <AdminLayout title="Reviews" titleTr={t('admin.rev_page_title')}>
      <PageHeader
        title={t('admin.rev_page_title')}
        subtitle={t('admin.rev_page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.rev_page_title') }]}
      />

      {loading ? (
        <LoadingState variant="table" rows={5} cols={5} />
      ) : (
        <DataTable
          columns={columns}
          data={reviews}
          rowKey="review_id"
          emptyIcon={<Star size={40} strokeWidth={1.5} />}
          emptyTitle={t('admin.rev_empty_title')}
          emptyDescription={t('admin.rev_empty_desc')}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setConfirmId(null); } }}
        title={t('admin.rev_delete_title')}
        description={t('admin.rev_delete_desc')}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleConfirmDelete}
        loading={confirmLoading}
      />
    </AdminLayout>
  );
}
